import { useEffect, useState } from 'react';

import { Formik } from 'formik';
import moment from 'jalali-moment';
import * as Yup from 'yup';

import Datepicker from '../../Shared/Components/Datepicker';
import ErrorMessage from '../../Shared/Components/ErrorMessage';
import FileUploader from '../../Shared/Components/FileUploader';
import { error } from '../../Shared/Components/Toast';
import useApi from '../../Shared/hooks/api/Index';
import { getStoredAuthToken } from '../../Shared/utils/authToken';

export const MARRIED_OPTIONS = [
  { value: '0', label: 'مجرد' },
  { value: '1', label: 'متاهل' },
];

export const ARMY_OPTIONS = [
  { value: '1', label: 'پایان خدمت' },
  { value: '2', label: 'معافیت' },
  { value: '3', label: 'مشمول' },
  { value: '4', label: 'در حال خدمت' },
];

export const RELIGION_OPTIONS = [
  { value: '1', label: 'اسلام' },
  { value: '2', label: 'مسیحی' },
  { value: '3', label: 'کلیمی' },
  { value: '4', label: 'زرتشتی' },
];

export const SACRIFICE_OPTIONS = [
  { value: '1', label: 'جانباز' },
  { value: '2', label: 'آزاده' },
  { value: '3', label: 'خانواده شهید' },
  { value: '4', label: 'رزمنده' },
  { value: '5', label: 'عادی / ندارد' },
];

export const PHYSICAL_CONDITION_OPTIONS = [
  { value: '1', label: 'سالم' },
  { value: '0', label: 'دارای معلولیت' },
];

export const userProfileValidationSchema = Yup.object().shape({
  name: Yup.string()
    .required('نام الزامی است')
    .max(190, 'نام نمیتواند بیشتر از 190 کاراکتر باشد'),
  last_name: Yup.string()
    .required('نام خانوادگی الزامی است')
    .max(190, 'نام خانوادگی نمیتواند بیشتر از 190 کاراکتر باشد'),
  name_en: Yup.string().nullable().max(190, 'نام لاتین نمیتواند بیشتر از 190 کاراکتر باشد'),
  last_name_en: Yup.string()
    .nullable()
    .max(190, 'نام خانوادگی لاتین نمیتواند بیشتر از 190 کاراکتر باشد'),
  father_name: Yup.string().nullable().max(190, 'نام پدر نمیتواند بیشتر از 190 کاراکتر باشد'),
  father_name_en: Yup.string()
    .nullable()
    .max(190, 'نام پدر لاتین نمیتواند بیشتر از 190 کاراکتر باشد'),
  certificate_id: Yup.string()
    .nullable()
    .max(50, 'شماره شناسنامه نمیتواند بیشتر از ۵۰ کاراکتر باشد'),
  mobile: Yup.string()
    .required('شماره موبایل الزامی است')
    .matches(/^09\d{9}$/, 'شماره موبایل معتبر نیست'),
  gender: Yup.number()
    .typeError('جنسیت الزامی است')
    .required('جنسیت الزامی است'),
  birthday: Yup.string().required('تاریخ تولد الزامی است'),
  province_living_id: Yup.number()
    .typeError('استان محل زندگی الزامی است')
    .required('استان محل زندگی الزامی است'),
  password: Yup.string()
    .nullable()
    .min(8, 'کلمه عبور باید حداقل ۸ کاراکتر باشد')
    .max(64, 'کلمه عبور نمی‌تواند بیشتر از ۶۴ کاراکتر باشد'),
});

const toSelectValue = (value) =>
  value === null || value === undefined || value === '' ? '' : String(value);

const buildInitialValues = (user) => ({
  name: user?.name ?? '',
  last_name: user?.last_name ?? '',
  name_en: user?.name_en ?? '',
  last_name_en: user?.last_name_en ?? '',
  father_name: user?.father_name ?? '',
  father_name_en: user?.father_name_en ?? '',
  certificate_id: user?.certificate_id ?? '',
  national_card_id: user?.national_card_id ?? '',
  mobile: user?.mobile ?? '',
  gender: toSelectValue(user?.gender),
  birthday:
    moment(user?.birthday ?? '1950-01-01', 'YYYY-MM-DD')
      .locale('fa')
      .format('YYYY/MM/DD') ?? '',
  countries_birth_code: user?.countries_birth_code ?? '',
  province_birth_id: toSelectValue(user?.province_birth_id?.id),
  city_birth_id: toSelectValue(user?.city_birth_id?.id),
  married: toSelectValue(user?.married),
  army: toSelectValue(user?.army),
  religion: toSelectValue(user?.religion),
  sacrifice: toSelectValue(user?.sacrifice),
  physical_condition: toSelectValue(user?.physical_condition),
  province_living_id: toSelectValue(user?.province_living_id?.id),
  password: '',
});

const UserProfileForm = ({
  user,
  provinces,
  countries = [],
  onSubmit,
  submitLoading,
  photoIds,
  setPhotoIds,
  insuranceIds,
  setInsuranceIds,
  uploadProfileUrl,
  uploadInsuranceUrl,
  showIncompleteWarning = false,
}) => {
  const [birthCities, setBirthCities] = useState([]);
  const initialProvinceBirthId = user?.province_birth_id?.id ?? '';

  useEffect(() => {
    if (!initialProvinceBirthId) {
      setBirthCities([]);
      return;
    }

    let cancelled = false;
    useApi.get(
      `/user/portal/cities/${initialProvinceBirthId}`,
      {},
      (res) => {
        if (!cancelled) {
          setBirthCities(res.data.data || []);
        }
      },
      () => {
        if (!cancelled) {
          setBirthCities([]);
          error('هنگام دریافت شهرهای محل تولد خطایی رخ داده است.');
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, [initialProvinceBirthId]);

  const loadBirthCities = (provinceId, setFieldValue) => {
    const nextProvinceId = provinceId ? String(provinceId) : '';
    setFieldValue('province_birth_id', nextProvinceId);
    setFieldValue('city_birth_id', '');

    if (!nextProvinceId) {
      setBirthCities([]);
      return;
    }

    useApi.get(
      `/user/portal/cities/${nextProvinceId}`,
      {},
      (res) => {
        setBirthCities(res.data.data || []);
      },
      () => {
        setBirthCities([]);
        error('هنگام دریافت شهرهای محل تولد خطایی رخ داده است.');
      },
    );
  };

  return (
    <>
      {showIncompleteWarning && !user?.profile_complete && (
        <div className='alert alert-warning f-13 my-3'>
          {user?.require_documents !== false
            ? 'برای ثبت‌نام در مسابقات، لطفاً تمام فیلدهای زیر از جمله عکس پرسنلی و تصویر بیمه ورزشی را تکمیل کنید.'
            : 'برای ثبت‌نام در مسابقات، لطفاً فیلدهای الزامی پروفایل را تکمیل کنید.'}
          {user?.profile_missing_fields?.length > 0 && (
            <div className='mt-2'>
              موارد ناقص: {user.profile_missing_fields.join('، ')}
            </div>
          )}
        </div>
      )}
      <Formik
        initialValues={buildInitialValues(user)}
        onSubmit={(values) => onSubmit(values)}
        validationSchema={userProfileValidationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <div className='row border rounded mt-3 p-2'>
              <div className='col-12'>
                <h2 className='f-14 text-secondary font-weight-bold mt-2 mb-3'>
                  اطلاعات هویتی
                </h2>
              </div>

              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>نام</label>
                    <input
                      name='name'
                      value={values.name}
                      onChange={handleChange}
                      type='text'
                      className='form-control'
                    />
                    <ErrorMessage error={errors.name} />
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>نام خانوادگی</label>
                    <input
                      name='last_name'
                      value={values.last_name}
                      onChange={handleChange}
                      type='text'
                      className='form-control'
                    />
                    <ErrorMessage error={errors.last_name} />
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>نام لاتین</label>
                    <input
                      name='name_en'
                      value={values.name_en}
                      onChange={handleChange}
                      type='text'
                      className='form-control'
                    />
                    <ErrorMessage error={errors.name_en} />
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>نام خانوادگی لاتین</label>
                    <input
                      name='last_name_en'
                      value={values.last_name_en}
                      onChange={handleChange}
                      type='text'
                      className='form-control'
                    />
                    <ErrorMessage error={errors.last_name_en} />
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>نام پدر</label>
                    <input
                      name='father_name'
                      value={values.father_name}
                      onChange={handleChange}
                      type='text'
                      className='form-control'
                    />
                    <ErrorMessage error={errors.father_name} />
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>نام پدر لاتین</label>
                    <input
                      name='father_name_en'
                      value={values.father_name_en}
                      onChange={handleChange}
                      type='text'
                      className='form-control'
                    />
                    <ErrorMessage error={errors.father_name_en} />
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>کد ملی</label>
                    <input
                      name='national_card_id'
                      value={values.national_card_id}
                      readOnly
                      type='text'
                      className='form-control bg-light'
                    />
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>شماره شناسنامه</label>
                    <input
                      name='certificate_id'
                      value={values.certificate_id}
                      onChange={handleChange}
                      type='text'
                      className='form-control'
                    />
                    <ErrorMessage error={errors.certificate_id} />
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>شماره موبایل</label>
                    <input
                      name='mobile'
                      value={values.mobile}
                      onChange={handleChange}
                      type='text'
                      className='form-control'
                    />
                    <ErrorMessage error={errors.mobile} />
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>جنسیت</label>
                    <select
                      className='form-control'
                      name='gender'
                      onChange={handleChange}
                      value={values.gender}
                    >
                      <option value=''>انتخاب کنید</option>
                      <option value='1'>مرد</option>
                      <option value='0'>زن</option>
                    </select>
                    <ErrorMessage error={errors.gender} />
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>تاریخ تولد</label>
                    <Datepicker
                      value={values.birthday}
                      closeOnSelect
                      onChange={(value) => setFieldValue('birthday', value, true)}
                    />
                    <ErrorMessage error={errors.birthday} />
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>کشور محل تولد</label>
                    <select
                      className='form-control'
                      name='countries_birth_code'
                      onChange={handleChange}
                      value={values.countries_birth_code}
                    >
                      <option value=''>انتخاب کنید</option>
                      {countries?.map((item) => (
                        <option key={item.code || item.id} value={item.code}>
                          {item.title || item.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>استان محل تولد</label>
                    <select
                      className='form-control'
                      name='province_birth_id'
                      value={values.province_birth_id}
                      onChange={(e) => loadBirthCities(e.target.value, setFieldValue)}
                    >
                      <option value=''>انتخاب کنید</option>
                      {provinces?.map((item) => (
                        <option key={item.id} value={String(item.id)}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>شهر محل تولد</label>
                    <select
                      className='form-control'
                      name='city_birth_id'
                      onChange={handleChange}
                      value={values.city_birth_id}
                      disabled={!values.province_birth_id}
                    >
                      <option value=''>انتخاب کنید</option>
                      {birthCities.map((item) => (
                        <option key={item.id} value={String(item.id)}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>وضعیت تاهل</label>
                    <select
                      className='form-control'
                      name='married'
                      onChange={handleChange}
                      value={values.married}
                    >
                      <option value=''>انتخاب کنید</option>
                      {MARRIED_OPTIONS.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>وضعیت نظام وظیفه</label>
                    <select
                      className='form-control'
                      name='army'
                      onChange={handleChange}
                      value={values.army}
                    >
                      <option value=''>انتخاب کنید</option>
                      {ARMY_OPTIONS.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>دین</label>
                    <select
                      className='form-control'
                      name='religion'
                      onChange={handleChange}
                      value={values.religion}
                    >
                      <option value=''>انتخاب کنید</option>
                      {RELIGION_OPTIONS.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>وضعیت ایثارگری</label>
                    <select
                      className='form-control'
                      name='sacrifice'
                      onChange={handleChange}
                      value={values.sacrifice}
                    >
                      <option value=''>انتخاب کنید</option>
                      {SACRIFICE_OPTIONS.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>وضعیت جسمانی</label>
                    <select
                      className='form-control'
                      name='physical_condition'
                      onChange={handleChange}
                      value={values.physical_condition}
                    >
                      <option value=''>انتخاب کنید</option>
                      {PHYSICAL_CONDITION_OPTIONS.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>استان محل زندگی</label>
                    <select
                      className='form-control'
                      name='province_living_id'
                      onChange={handleChange}
                      value={values.province_living_id}
                    >
                      <option value=''>انتخاب کنید</option>
                      {provinces?.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    <ErrorMessage error={errors.province_living_id} />
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>کلمه عبور جدید</label>
                    <input
                      name='password'
                      value={values.password}
                      onChange={handleChange}
                      type='password'
                      autoComplete='new-password'
                      className='form-control'
                      placeholder='در صورت نیاز به تغییر وارد کنید'
                    />
                    <ErrorMessage error={errors.password} />
                  </div>
                </div>
              </div>

              <div className='col-12'>
                <h2 className='f-14 text-secondary font-weight-bold mt-3 mb-3'>
                  مدارک
                </h2>
              </div>

              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    {user?.personnel_image && (
                      <img
                        style={{ width: '100px', height: '100px' }}
                        className='img-thumbnail rounded my-2'
                        src={user.personnel_image}
                        alt='personnel'
                      />
                    )}
                    <label>عکس پرسنلی</label>
                    <FileUploader
                      files={photoIds}
                      roles={['image/jpeg', 'image/jpg', 'image/png']}
                      setFiles={setPhotoIds}
                      allowMultiple={false}
                      maxFiles={1}
                      maxFileSize='2048KB'
                      name='personnel_image'
                      serverUrl={uploadProfileUrl}
                      method='POST'
                      headers={{
                        Accept: 'application/json',
                        Authorization: 'Bearer ' + getStoredAuthToken(),
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    {user?.insurance_image && (
                      <img
                        style={{ width: '100px', height: '100px' }}
                        className='img-thumbnail rounded my-2'
                        src={user.insurance_image}
                        alt='insurance'
                      />
                    )}
                    <label>تصویر بیمه ورزشی</label>
                    <FileUploader
                      files={insuranceIds}
                      roles={['image/jpeg', 'image/jpg', 'image/png']}
                      setFiles={setInsuranceIds}
                      allowMultiple={false}
                      maxFiles={1}
                      maxFileSize='2048KB'
                      name='insurance_image'
                      serverUrl={uploadInsuranceUrl}
                      method='POST'
                      headers={{
                        Accept: 'application/json',
                        Authorization: 'Bearer ' + getStoredAuthToken(),
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className='col-12'>
                <div className='d-flex flex-row justify-content-end my-3'>
                  <button type='submit' className='btn btn-info f-14'>
                    {submitLoading ? (
                      <div
                        className='spinner-border spinner-border-sm text-white mb-1'
                        role='status'
                      ></div>
                    ) : (
                      'ثبت اطلاعات'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </>
  );
};

export default UserProfileForm;
